import { useMemo, useState } from "react"
import { Heart, MessageCircle, BadgeCheck, X } from "lucide-react"
import { likeCommunityPost } from "../../community/community-service"
import { formatTimeAgo, getAvatarInitialColor } from "../../community/community-ui-utils"
import CommunityPostComments from "./CommunityPostComments"

const CHANNEL_PILL = {
  general: "bg-emerald-500/15 text-emerald-200",
  profitloss: "bg-blue-500/15 text-blue-200",
}

const CHANNEL_LABEL = {
  general: "General",
  profitloss: "Profit & Loss",
}

function MessageWithTags({ message, onTagClick }) {
  const parts = useMemo(() => {
    const result = []
    const regex = /#(\w+)/g
    let lastIndex = 0
    let m
    while ((m = regex.exec(message)) !== null) {
      const start = m.index
      const raw = m[0] // includes '#'
      const tag = m[1]
      if (start > lastIndex) result.push({ type: "text", value: message.slice(lastIndex, start) })
      result.push({ type: "tag", value: tag, raw })
      lastIndex = start + raw.length
    }
    if (lastIndex < message.length) result.push({ type: "text", value: message.slice(lastIndex) })
    return result
  }, [message])

  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((p, i) => {
        if (p.type === "text") return <span key={i}>{p.value}</span>
        return (
          <button
            key={i}
            type="button"
            onClick={() => onTagClick?.(p.value)}
            className="font-medium text-blue-300 hover:underline"
          >
            #{p.value}
          </button>
        )
      })}
    </span>
  )
}

export default function CommunityPostCard({ post, onTagClick, onCommentAdded, onLikeAdded }) {
  const [enlargedImage, setEnlargedImage] = useState(null)
  const [liking, setLiking] = useState(false)

  const handleLike = async () => {
    if (liking) return
    setLiking(true)
    try {
      await likeCommunityPost(post.id)
      onLikeAdded?.(post.id)
    } catch (err) {
      console.error("Like failed:", err)
    } finally {
      setLiking(false)
    }
  }

  return (
    <div className="group border-b border-gray-800/60 py-3 last:border-b-0 hover:bg-gray-800/20 px-1">
      <div className="flex gap-3 px-1">
        <div className="shrink-0">
          {post.avatar ? (
            <img
              src={post.avatar}
              alt=""
              className="h-9 w-9 rounded-full object-cover border border-gray-800/60"
            />
          ) : (
            <div
              className={[
                "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold border border-gray-800/60",
                getAvatarInitialColor(post.uid || post.username || "?"),
              ].join(" ")}
            >
              {(post.username || "?")[0]?.toUpperCase()}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-semibold text-white/90">{post.username}</span>

            {post.verified ? (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-1 text-emerald-200">
                <BadgeCheck className="h-4 w-4 shrink-0" />
                <span className="text-[11px] font-medium">Verified</span>
              </span>
            ) : null}

            <span className="text-[11px] text-gray-400">{formatTimeAgo(post.createdAt)}</span>
            <span className="text-[11px] text-gray-500">·</span>

            <span
              className={[
                "inline-block px-2 py-0.5 text-[11px] font-medium rounded-md",
                CHANNEL_PILL[post.channel] ?? "bg-gray-800 text-gray-300",
              ].join(" ")}
            >
              {CHANNEL_LABEL[post.channel] ?? post.channel}
            </span>
          </div>

          <p className="mt-1 text-sm text-white/90 leading-snug">
            <MessageWithTags message={post.message} onTagClick={onTagClick} />
          </p>

          {post.images && post.images.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {post.images.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setEnlargedImage(url)}
                  className="block overflow-hidden rounded border border-gray-800 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  <img src={url} alt="" className="h-32 w-32 object-cover" />
                </button>
              ))}
            </div>
          ) : null}

          {post.tags && post.tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {post.tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => onTagClick?.(t)}
                  className="rounded border border-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-200 hover:bg-gray-800/50"
                >
                  #{t}
                </button>
              ))}
            </div>
          ) : null}

          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={handleLike}
              disabled={liking}
              className="flex items-center gap-2 text-xs text-gray-400 hover:text-emerald-200 disabled:opacity-50"
              aria-label="Like post"
            >
              <Heart className="h-4 w-4 shrink-0 text-rose-400" />
              <span>{post.likes}</span>
            </button>

            <div className="flex items-center gap-1 text-xs text-gray-400">
              <MessageCircle className="h-4 w-4 shrink-0 text-emerald-300" />
              <span>{post.replyCount}</span>
            </div>
          </div>

          <CommunityPostComments post={post} onCommentAdded={onCommentAdded} />
        </div>
      </div>

      {enlargedImage ? (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <button
            type="button"
            onClick={() => setEnlargedImage(null)}
            className="absolute top-4 right-4 h-9 w-9 rounded-md bg-gray-900/70 border border-gray-800 text-white hover:bg-gray-900 transition-colors flex items-center justify-center"
            aria-label="Close image"
          >
            <X className="h-4 w-4" />
          </button>
          <img src={enlargedImage} alt="" className="max-h-[85vh] w-auto rounded-lg border border-gray-800" />
        </div>
      ) : null}
    </div>
  )
}

